import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import weightChart from '~/assets/images/weight-chart.png';
import {Col, Row, UiImage, UiListItem, UiQuestionnaireLayout, UiText, UiUnorderedList} from '~/components/elements';
import BoldText from '~/components/shared/BoldText';
import {PAGE_URLS} from '~/constants';
import {WEIGHT_LOSS_KEYS, WEIGHT_LOSS_ONE_QUESTIONNAIRES, WL_PROGRAMS} from '~/constants/medical-questionnaires';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';

interface Props {}

const Questionnaire9Screen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_ONE_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_KEYS.ELEVATE_PROGRAM_GOALS);

  const bmiAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.BMI_CALCULATOR]?.data,
  ) || {height: 0, weight: 0, bmi: 0};

  const selectedProgram = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.PROGRAM_SELECTION]?.data?.selectedProgramLocal,
  );

  const isGLPProgram = selectedProgram === WL_PROGRAMS.ELEVATE_GLP_1_PROGRAM.localValue;
  const isSaxenda = selectedProgram === WL_PROGRAMS.ELEVATE_SAXENDA.localValue;
  const weightLossRate = isGLPProgram || selectedProgram ? 85 : 90;

  const userWeight = bmiAnswer?.weight || 0;
  const goalWeight = Math.round(userWeight * (weightLossRate / 100));
  const bottomWeight = Math.round(goalWeight - 1);
  const middleWeight = Math.round((userWeight + bottomWeight) / 2);

  const programData = Object.values(WL_PROGRAMS).find(i => i.value === selectedProgram);
  const programTitle = programData?.title || '';
  const programName = programData?.goalName || '';

  const isSupplement =
    selectedProgram === WL_PROGRAMS.ELEVATE_CGM_PROGRAM.localValue ||
    selectedProgram === WL_PROGRAMS.ELEVATE_SUPPLEMENT_PROGRAM.localValue;

  const collectedData = mdq.collectedData;

  const onClickSubmit = () => {
    const answerData = {
      key: WEIGHT_LOSS_KEYS.ELEVATE_PROGRAM_GOALS,
      index: questionnaire?.index || '',
      data: {userWeight, goalWeight, bottomWeight, middleWeight},
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.ELEVATE_PROGRAM_GOALS]: answerData,
          },
        },
      }),
    );
    // dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.METABOLIC_ASSESSMENT}));

    if (selectedProgram === WL_PROGRAMS.ELEVATE_CGM_PROGRAM.localValue) {
      history.push(PAGE_URLS.MDQ1_SELECT_CGM_PACKAGE);
      return;
    }

    if (selectedProgram === WL_PROGRAMS.ELEVATE_SAXENDA.localValue) {
      history.push(PAGE_URLS.MDQ1_SELECT_SAXENDA_PACKAGE);
      return;
    }

    if (selectedProgram === WL_PROGRAMS.ELEVATE_SUPPLEMENT_PROGRAM.localValue) {
      history.push(PAGE_URLS.MDQ1_PHARMACIST_CONSULTATION);
      return;
    }

    if (isGLPProgram) {
      history.push(PAGE_URLS.MDQ1_DOCTOR_CONSULTATION_GLP);
      return;
    }

    history.push(PAGE_URLS.MDQ1_DOCTOR_CONSULTATION_PILL);
  };

  const cgmNote = `*The above is a simplified projection based on scientific studies of behavioral change programs and their impact on weight loss. Individual results may vary.`;
  const glpNote = `*The above is a simplified projection based on scientific studies of GLP and its impact on weight loss. Individual results may vary.`;

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      handleOnNext={onClickSubmit}
      pageTitle={questionnaire?.title + ' ' + programTitle}
    >
      <Col alignItems="center" textAlign="center" mt={12} px={2}>
        <BoldText
          fontSize="xl"
          fontWeight="bold"
          maxW={80}
          mb={12}
          text={
            questionnaire?.questions?.default.replace('[kg]', String(goalWeight)).replace('[program]', programName) ||
            ''
          }
          boldTextStyles={{color: '#CCDF75'}}
        />
        <Col>
          <Row>
            <Col justifyContent="center" mr={4}>
              <UiText color="lightBlack">{userWeight}</UiText>
              <UiText color="lightBlack" my={{base: '3.5rem', sm: '4rem', lg: '4rem'}}>
                {middleWeight}
              </UiText>
              <UiText color="lightBlack">{bottomWeight}</UiText>
            </Col>
            <Col position="relative">
              <UiText position="absolute" textAlign="left" fontWeight="600" left={-9}>
                Your weight (kg)
              </UiText>
              <UiText
                position="absolute"
                fontWeight="bold"
                py={1}
                px={3}
                bg="trueBlack"
                color="lightBaseColor"
                right={-3}
                bottom="30%"
                borderRadius={12}
              >
                {goalWeight} kg
              </UiText>
              <UiImage height="17rem" width="18rem" zIndex={3} url={weightChart} position="relative" />
              <UiText
                fontSize="xs"
                color="black"
                position="absolute"
                bottom={isSupplement ? -8 : -5}
                textAlign="left"
                opacity={0.6}
                pr={4}
              >
                {isSupplement ? cgmNote : glpNote}
              </UiText>
            </Col>
          </Row>
        </Col>
        {isSaxenda && (
          <Col textAlign="left" px={6} mt={12}>
            <UiText mb={2.5} fontWeight={500}>
              {questionnaire?.descriptions?.sub}
            </UiText>
            <UiUnorderedList textAlign="left">
              {questionnaire?.descriptions?.default?.map(desc => (
                <UiListItem key={desc} mb={2.5}>
                  <BoldText text={desc} fontSize="xs" />
                </UiListItem>
              ))}
            </UiUnorderedList>
          </Col>
        )}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire9Screen;
